<template>
  <div class="worker-profile">
    <el-container>
      <!-- 顶部导航 -->
      <el-header class="header">
        <div class="header-left">
          <el-button type="text" @click="goBack" class="back-btn">
            <i class="el-icon-arrow-left"></i>
            返回
          </el-button>
          <h2>个人信息</h2>
        </div>
        <div class="header-right">
          <el-button type="primary" @click="editMode = !editMode" size="small">
            <i class="el-icon-edit"></i>
            {{ editMode ? '取消编辑' : '编辑资料' }}
          </el-button>
        </div>
      </el-header>

      <!-- 主内容区 -->
      <el-main class="main-content">
        <!-- 个人头像和基本信息 -->
        <div class="profile-header">
          <div class="avatar-section">
            <div class="avatar-wrapper">
              <img :src="userProfile.avatar || defaultAvatar" alt="头像" class="avatar">
              <div v-if="editMode" class="avatar-upload" @click="uploadAvatar">
                <i class="el-icon-camera"></i>
                <span>更换头像</span>
              </div>
            </div>
            <div class="basic-info">
              <h3>{{ userProfile.name }}</h3>
              <div class="worker-id">工号：{{ userProfile.username }}</div>
              <div class="join-date">入职时间：{{ formatDate(userProfile.created_at) }}</div>
            </div>
          </div>
          
          <!-- 工作状态 -->
          <div class="work-status">
            <el-tag :type="getStatusTagType(userProfile.status)" size="large">
              {{ getStatusText(userProfile.status) }}
            </el-tag>
            <div class="rating">
              <el-rate 
                v-model="userProfile.average_rating" 
                disabled 
                show-score 
                text-color="#ff9900"
                score-template="{value}分">
              </el-rate>
            </div>
          </div>
        </div>

        <!-- 详细信息表单 -->
        <div class="profile-form">
          <el-card class="info-card" header="基本信息">
            <el-form :model="profileForm" label-width="100px" :disabled="!editMode">
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="姓名">
                    <el-input v-model="profileForm.name" placeholder="请输入姓名"></el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="手机号">
                    <el-input v-model="profileForm.phone" placeholder="请输入手机号"></el-input>
                  </el-form-item>
                </el-col>
              </el-row>

              <el-form-item label="技能描述">
                <el-input v-model="profileForm.skills" type="textarea" :rows="3" placeholder="请输入技能描述"></el-input>
              </el-form-item>
            </el-form>
          </el-card>



          <!-- 工作统计 -->
          <el-card class="info-card" header="工作统计">
            <el-row :gutter="20" class="stats-grid">
              <el-col :span="6">
                <div class="stat-item">
                  <div class="stat-number">{{ userProfile.total_orders }}</div>
                  <div class="stat-label">总工单数</div>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="stat-item">
                  <div class="stat-number">{{ userProfile.completed_orders }}</div>
                  <div class="stat-label">已完成</div>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="stat-item">
                  <div class="stat-number">{{ userProfile.in_progress_orders }}</div>
                  <div class="stat-label">进行中</div>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="stat-item">
                  <div class="stat-number">{{ userProfile.pending_orders }}</div>
                  <div class="stat-label">可接工单</div>
                </div>
              </el-col>
            </el-row>
            
            <el-row :gutter="20" class="stats-grid" style="margin-top: 20px;">
              <el-col :span="8">
                <div class="stat-item">
                  <div class="stat-number">{{ (userProfile.completion_rate * 100).toFixed(1) }}%</div>
                  <div class="stat-label">完成率</div>
                </div>
              </el-col>
              <el-col :span="8">
                <div class="stat-item">
                  <div class="stat-number">{{ userProfile.average_rating.toFixed(1) }}</div>
                  <div class="stat-label">平均评分</div>
                </div>
              </el-col>
              <el-col :span="8">
                <div class="stat-item">
                  <div class="stat-number">¥{{ formatMoney(userProfile.total_revenue) }}</div>
                  <div class="stat-label">总收入</div>
                </div>
              </el-col>
            </el-row>
          </el-card>


        </div>

        <!-- 操作按钮 -->
        <div class="action-buttons" v-if="editMode">
          <el-button @click="cancelEdit">取消</el-button>
          <el-button type="primary" @click="saveProfile" :loading="saving">保存修改</el-button>
        </div>
      </el-main>
    </el-container>

    <!-- 头像上传对话框 -->
    <el-dialog title="更换头像" :visible.sync="avatarDialogVisible" width="400px">
      <div class="avatar-upload-dialog">
        <el-upload
          class="avatar-uploader"
          :action="uploadUrl"
          :show-file-list="false"
          :on-success="handleAvatarSuccess"
          :before-upload="beforeAvatarUpload">
          <img v-if="previewAvatar" :src="previewAvatar" class="avatar-preview">
          <i v-else class="el-icon-plus avatar-uploader-icon"></i>
        </el-upload>
        <div class="upload-tips">
          <p>支持 JPG、PNG 格式</p>
          <p>文件大小不超过 2MB</p>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="avatarDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmAvatar" :disabled="!previewAvatar">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'WorkerProfile',
  data() {
    return {
      editMode: false,
      saving: false,
      avatarDialogVisible: false,
      previewAvatar: '',
      uploadUrl: '/api/upload/avatar',
      defaultAvatar: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxjaXJjbGUgY3g9IjUwIiBjeT0iMzUiIHI9IjE1IiBmaWxsPSIjQ0NDIi8+CjxwYXRoIGQ9Ik0yMCA4MEMyMCA2NS44NTc5IDMxLjQzMTUgNTUgNDUgNTVINTVDNjguNTY4NSA1NSA4MCA2NS44NTc5IDgwIDgwVjEwMEgyMFY4MFoiIGZpbGw9IiNDQ0MiLz4KPC9zdmc+',
      
      // 用户资料表单 - 基于workers表结构
      profileForm: {
        name: '',
        phone: '',
        skills: ''
      },

      // 用户资料数据 - 基于数据库表结构
      userProfile: {
        id: null,
        username: '',
        name: '',
        phone: '',
        skills: '',
        commission_rate: 0,
        status: 'active',
        created_at: '',
        last_login: '',
        hourly_rate: 0,
        overtime_rate: 0,
        // 统计数据
        total_orders: 0,
        completed_orders: 0,
        pending_orders: 0,
        in_progress_orders: 0,
        completion_rate: 0,
        average_rating: 0,
        total_revenue: 0
      }
    }
  },
  computed: {
    ...mapGetters(['currentUser'])
  },
  mounted() {
    this.loadUserProfile()
  },
  methods: {
    goBack() {
      this.$safeRouter.push('/worker/home')
    },

    async loadUserProfile() {
      try {
        // 获取工人基本信息
        const profileResponse = await this.$http.get('/api/worker/profile')
        if (profileResponse.data) {
          this.userProfile = {
            ...this.userProfile,
            ...profileResponse.data
          }
          
          // 初始化表单数据
          this.profileForm = {
            name: this.userProfile.name,
            phone: this.userProfile.phone,
            skills: this.userProfile.skills
          }
        }

        // 获取工作统计数据
        await this.loadWorkStats()
      } catch (error) {
        console.error('加载个人信息失败:', error)
        this.$message.error('加载个人信息失败')
      }
    },

    async loadWorkStats() {
      try {
        const statsResponse = await this.$http.get('/api/worker/stats')
        if (statsResponse.data) {
          Object.assign(this.userProfile, statsResponse.data)
        }
      } catch (error) {
        console.error('加载工作统计失败:', error)
      }
    },

    // 编辑模式相关
    cancelEdit() {
      this.editMode = false
      this.profileForm = {
        name: this.userProfile.name,
        phone: this.userProfile.phone,
        skills: this.userProfile.skills
      }
    },

    async saveProfile() {
      this.saving = true
      try {
        // 调用API保存用户信息
        const response = await this.$http.put('/api/worker/profile', {
          name: this.profileForm.name,
          phone: this.profileForm.phone,
          skills: this.profileForm.skills
        })
        
        if (response.success) {
          // 更新本地用户信息
          this.userProfile.name = this.profileForm.name
          this.userProfile.phone = this.profileForm.phone
          this.userProfile.skills = this.profileForm.skills
          
          this.$message.success('个人信息保存成功')
          this.editMode = false
        } else {
          throw new Error(response.message || '保存失败')
        }
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error('保存失败：' + (error.response?.data?.message || error.message))
      } finally {
        this.saving = false
      }
    },

    // 头像上传相关
    uploadAvatar() {
      this.avatarDialogVisible = true
    },

    handleAvatarSuccess(res, file) {
      this.previewAvatar = URL.createObjectURL(file.raw)
    },

    beforeAvatarUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isJPG) {
        this.$message.error('头像图片只能是 JPG/PNG 格式!')
      }
      if (!isLt2M) {
        this.$message.error('头像图片大小不能超过 2MB!')
      }
      return isJPG && isLt2M
    },

    confirmAvatar() {
      this.userProfile.avatar = this.previewAvatar
      this.avatarDialogVisible = false
      this.$message.success('头像更新成功')
    },

    // 工具方法
    formatDate(date) {
      if (!date) return '未知'
      return new Date(date).toLocaleDateString('zh-CN')
    },

    formatMoney(amount) {
      if (!amount) return '0.00'
      return parseFloat(amount).toFixed(2)
    },

    getStatusTagType(status) {
      const typeMap = {
        'active': 'success',
        'inactive': 'warning',
        'suspended': 'danger'
      }
      return typeMap[status] || 'info'
    },

    getStatusText(status) {
      const textMap = {
        'active': '在职',
        'inactive': '休假',
        'suspended': '停职'
      }
      return textMap[status] || '未知'
    }
  }
}
</script>

<style scoped>
.worker-profile {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.header {
  background: linear-gradient(135deg, #67C23A 0%, #85ce61 100%);
  color: white;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.back-btn {
  color: white !important;
  font-size: 16px;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

.header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.main-content {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

/* 个人头像和基本信息 */
.profile-header {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.8));
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.avatar-section {
  display: flex;
  align-items: center;
  gap: 20px;
}

.avatar-wrapper {
  position: relative;
}

.avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #67C23A;
  box-shadow: 0 4px 15px rgba(103, 194, 58, 0.3);
}

.avatar-upload {
  position: absolute;
  bottom: 0;
  right: 0;
  background: #67C23A;
  color: white;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.basic-info h3 {
  margin: 0 0 10px 0;
  font-size: 24px;
  color: #2c3e50;
  font-weight: 700;
}

.worker-id, .join-date {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 5px;
}

.work-status {
  text-align: right;
}

.work-status .el-tag {
  margin-bottom: 10px;
}

.rating {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* 信息卡片 */
.info-card {
  margin-bottom: 20px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.info-card >>> .el-card__header {
  background: linear-gradient(135deg, #67C23A 0%, #85ce61 100%);
  color: white;
  font-weight: 600;
  font-size: 16px;
}

.info-card >>> .el-card__body {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
}



/* 工作统计 */
.stats-grid {
  margin: 0;
}

.stat-item {
  text-align: center;
  padding: 20px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.stat-number {
  font-size: 28px;
  font-weight: 800;
  color: #67C23A;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  color: #6c757d;
  font-weight: 500;
}



/* 操作按钮 */
.action-buttons {
  text-align: center;
  margin-top: 30px;
}

.action-buttons .el-button {
  min-width: 120px;
  margin: 0 10px;
}

/* 头像上传对话框 */
.avatar-upload-dialog {
  text-align: center;
}

.avatar-uploader >>> .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 178px;
  height: 178px;
  margin: 0 auto;
}

.avatar-uploader >>> .el-upload:hover {
  border-color: #67C23A;
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  line-height: 178px;
  text-align: center;
}

.avatar-preview {
  width: 178px;
  height: 178px;
  display: block;
  object-fit: cover;
}

.upload-tips {
  margin-top: 15px;
  color: #999;
  font-size: 12px;
}

.upload-tips p {
  margin: 5px 0;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .worker-profile {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    min-height: 100vh;
  }

  .main-content {
    padding: 15px;
    padding-bottom: 80px; /* 防止被底部元素遮挡 */
  }

  .profile-header {
    flex-direction: column;
    text-align: center;
    gap: 20px;
    padding: 25px 20px;
    margin-bottom: 15px;
  }

  .avatar-section {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }

  .work-status {
    text-align: center;
  }



  .stats-grid .el-col {
    margin-bottom: 15px;
  }

  .stats-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .stats-grid .el-col {
    flex: 1;
    min-width: calc(50% - 5px);
    margin-bottom: 10px;
  }

  .header {
    padding: 0 15px;
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .header h2 {
    font-size: 18px;
  }

  .header-left {
    gap: 10px;
  }

  .avatar {
    width: 80px;
    height: 80px;
  }

  .basic-info h3 {
    font-size: 20px;
    margin-bottom: 8px;
  }

  .stat-number {
    font-size: 24px;
  }

  .info-card {
    margin-bottom: 15px;
    border-radius: 12px;
  }

  .info-card >>> .el-card__body {
    padding: 15px;
  }

  .stat-item {
    padding: 15px 10px;
  }

  .action-buttons {
    margin-top: 20px;
    padding: 0 15px;
  }

  .action-buttons .el-button {
    min-width: 100px;
    margin: 5px;
  }

  /* 表单优化 */
  .info-card >>> .el-form-item {
    margin-bottom: 15px;
  }

  .info-card >>> .el-form-item__label {
    font-size: 14px;
    line-height: 1.5;
  }

  .info-card >>> .el-input__inner,
  .info-card >>> .el-textarea__inner {
    font-size: 14px;
    padding: 8px 12px;
  }
}

@media (max-width: 480px) {
  .main-content {
    padding: 10px;
    padding-bottom: 80px;
  }

  .profile-header {
    padding: 20px 15px;
    margin-bottom: 12px;
  }

  .info-card >>> .el-card__body {
    padding: 15px 12px;
  }

  .info-card >>> .el-card__header {
    padding: 12px 15px;
    font-size: 15px;
  }

  .stat-item {
    padding: 12px 8px;
  }

  .stat-number {
    font-size: 20px;
  }

  .stat-label {
    font-size: 12px;
  }

  .action-buttons {
    margin-top: 15px;
    padding: 0 10px;
  }

  .action-buttons .el-button {
    min-width: 90px;
    margin: 3px;
    padding: 8px 15px;
    font-size: 13px;
  }

  .avatar {
    width: 70px;
    height: 70px;
  }

  .basic-info h3 {
    font-size: 18px;
  }

  .worker-id, .join-date {
    font-size: 13px;
  }

  /* 表单进一步优化 */
  .info-card >>> .el-form-item__label {
    font-size: 13px;
    width: 80px !important;
  }

  .info-card >>> .el-input__inner,
  .info-card >>> .el-textarea__inner {
    font-size: 13px;
    padding: 6px 10px;
  }

  .info-card >>> .el-row {
    margin: 0 -5px;
  }

  .info-card >>> .el-col {
    padding: 0 5px;
  }

  /* 评分组件优化 */
  .rating >>> .el-rate {
    height: auto;
  }

  .rating >>> .el-rate__item {
    margin-right: 3px;
  }
}

@media (max-width: 360px) {
  .main-content {
    padding: 8px;
    padding-bottom: 80px;
  }

  .profile-header {
    padding: 15px 10px;
  }

  .stats-grid .el-col {
    min-width: 100%;
    margin-bottom: 8px;
  }

  .info-card >>> .el-card__body {
    padding: 12px 10px;
  }

  .info-card >>> .el-form-item__label {
    width: 70px !important;
    font-size: 12px;
  }

  .action-buttons .el-button {
    min-width: 80px;
    font-size: 12px;
    padding: 6px 12px;
  }

  .avatar {
    width: 60px;
    height: 60px;
  }

  .basic-info h3 {
    font-size: 16px;
  }

  .stat-number {
    font-size: 18px;
  }

  .header h2 {
    font-size: 16px;
  }

  .back-btn {
    font-size: 14px;
  }
}
</style>